/**
 * STAGE TRUTH (screen-truth loop, slice 1) — the widget's ack half of
 * the presenter sync contract.
 *
 * The server GROUNDS every tourControl result on stage memory (the
 * latest present/ack), but its `next` is a prediction. This module
 * turns the player's real step events into `presenter-screen` session
 * events so stage memory tracks REALITY — including the viewer's own
 * clicks, which no server-side prediction can see. Source is classified
 * by recency: a step landing within AGENT_MOVE_WINDOW_MS of an applied
 * stage command is the agent's move; anything else is the viewer
 * driving. Acks are debounced (trailing, latest wins) so a burst of
 * auto-advance action beats posts once.
 */

export type StageTruthAck = {
  tourPublicId: string;
  screen: number;
  total?: number;
  source: 'agent' | 'viewer';
  /** Visible text of the screen (HTML screens), so the agent can quote
      what the viewer actually sees. Absent for image/video screens. */
  text?: string;
};

export type StageTruthDeps = {
  /** Post one ack (session event + optional voice note). */
  post: (ack: StageTruthAck) => void;
  /** Test seams; default to real clock/timers. */
  now?: () => number;
  setTimer?: (fn: () => void, ms: number) => ReturnType<typeof setTimeout>;
  clearTimer?: (t: ReturnType<typeof setTimeout>) => void;
};

/** A step within this window of an applied agent command is the agent's. */
export const AGENT_MOVE_WINDOW_MS = 3000;
/** Trailing debounce — a burst of beats acks once, latest wins. */
export const ACK_DEBOUNCE_MS = 600;

export type StageTruth = {
  /** The presenter router just APPLIED a stage command (present/control). */
  noteAgentMove(): void;
  /** The AGENT's last move brought this tour's stage to its final screen
      (the latest step for the tour was agent-sourced and screen === total).
      Timing-free on purpose: the player's `completed` fires when the last
      step ENDS (auto-advance or next past the end), long after the move. */
  agentLandedFinal(tourPublicId: string): boolean;
  /** A player step event: the stage is showing `screen` of `total`. */
  onStep(tourPublicId: string, screen: number, total?: number, text?: string): void;
  dispose(): void;
};

export function createStageTruth(deps: StageTruthDeps): StageTruth {
  const now = deps.now ?? (() => Date.now());
  const setTimer = deps.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
  const clearTimer = deps.clearTimer ?? ((t) => clearTimeout(t));

  let agentMoveAt = -Infinity;
  const agentFinal = new Set<string>();
  let pending: StageTruthAck | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let lastPosted: string | null = null;
  let disposed = false;

  const flush = () => {
    timer = null;
    if (disposed || !pending) return;
    const ack = pending;
    pending = null;
    // Dedupe on position only — a re-render of the same screen is noise.
    // Source is deliberately excluded: the classification can differ on
    // a repeat without the stage having moved.
    const key = `${ack.tourPublicId}#${ack.screen}`;
    if (key === lastPosted) return;
    lastPosted = key;
    deps.post(ack);
  };

  return {
    noteAgentMove(): void {
      agentMoveAt = now();
    },
    agentLandedFinal(tourPublicId: string): boolean {
      return agentFinal.has(tourPublicId);
    },
    onStep(tourPublicId: string, screen: number, total?: number, text?: string): void {
      if (disposed || !tourPublicId || !Number.isFinite(screen) || screen < 1) return;
      const source: 'agent' | 'viewer' = now() - agentMoveAt <= AGENT_MOVE_WINDOW_MS ? 'agent' : 'viewer';
      if (source === 'agent' && typeof total === 'number' && total > 0 && Math.floor(screen) >= Math.floor(total)) {
        agentFinal.add(tourPublicId);
      } else {
        agentFinal.delete(tourPublicId);
      }
      pending = {
        tourPublicId,
        screen: Math.floor(screen),
        ...(typeof total === 'number' && total > 0 ? { total: Math.floor(total) } : {}),
        source,
        ...(text ? { text } : {}),
      };
      if (timer != null) clearTimer(timer);
      timer = setTimer(flush, ACK_DEBOUNCE_MS);
    },
    dispose(): void {
      disposed = true;
      if (timer != null) clearTimer(timer);
      timer = null;
      pending = null;
    },
  };
}
